import { useState } from "react";
import { Plus, Trash2, GripVertical, Image as ImageIcon, FolderOpen, Upload } from "lucide-react";
import MediaPickerModal from "@/components/common/MediaPickerModal";

export interface GalleryImage {
  url: string;
  alt?: string;
  isPrimary?: boolean;
}

interface Props {
  value: GalleryImage[];
  onChange: (value: GalleryImage[]) => void;
}

export default function GalleryEditor({ value, onChange }: Props) {
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [newUrl, setNewUrl] = useState("");
  const [isMediaPickerOpen, setIsMediaPickerOpen] = useState(false);

  const addImage = () => {
    if (!newUrl.trim()) return;

    const newImage: GalleryImage = {
      url: newUrl.trim(),
      alt: "",
      isPrimary: value.length === 0,
    };
    onChange([...value, newImage]);
    setNewUrl("");
  };

  const addImagesFromLibrary = (urls: string[]) => {
    const newImages: GalleryImage[] = urls.map((url, i) => ({
      url,
      alt: "",
      isPrimary: value.length === 0 && i === 0,
    }));
    onChange([...value, ...newImages]);
  };

  const updateImage = (index: number, updates: Partial<GalleryImage>) => {
    const newValue = [...value];
    newValue[index] = { ...newValue[index], ...updates };
    onChange(newValue);
  };

  const removeImage = (index: number) => {
    const newValue = value.filter((_, i) => i !== index);
    // If removed image was primary, make first one primary
    if (value[index].isPrimary && newValue.length > 0) {
      newValue[0].isPrimary = true;
    }
    onChange(newValue);
  };

  const setPrimary = (index: number) => {
    const newValue = value.map((img, i) => ({
      ...img,
      isPrimary: i === index,
    }));
    onChange(newValue);
  };

  const moveImage = (fromIndex: number, toIndex: number) => {
    if (toIndex < 0 || toIndex >= value.length) return;

    const newValue = [...value];
    const [moved] = newValue.splice(fromIndex, 1);
    newValue.splice(toIndex, 0, moved);
    onChange(newValue);
  };

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex !== null && draggedIndex !== index) {
      moveImage(draggedIndex, index);
      setDraggedIndex(index);
    }
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          Galerie ({value.length} obrázků)
        </h3>
      </div>

      {/* Add new image */}
      <div className="flex flex-wrap gap-2">
        <input
          type="url"
          value={newUrl}
          onChange={(e) => setNewUrl(e.target.value)}
          placeholder="URL obrázku (https://...)"
          className="input flex-1 min-w-[200px]"
          onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addImage())}
        />
        <button
          type="button"
          onClick={addImage}
          disabled={!newUrl.trim()}
          className="btn-primary btn-sm"
        >
          <Plus className="h-4 w-4" />
          Přidat URL
        </button>
        <button
          type="button"
          onClick={() => setIsMediaPickerOpen(true)}
          className="btn-ghost btn-sm"
        >
          <FolderOpen className="h-4 w-4" />
          Z knihovny
        </button>
      </div>

      {/* Media Picker Modal */}
      <MediaPickerModal
        isOpen={isMediaPickerOpen}
        onClose={() => setIsMediaPickerOpen(false)}
        onSelect={() => {}}
        multiple
        onSelectMultiple={(urls) => {
          addImagesFromLibrary(urls);
          setIsMediaPickerOpen(false);
        }}
      />

      {value.length === 0 ? (
        <div className="rounded-lg border-2 border-dashed border-gray-300 p-8 text-center dark:border-gray-600">
          <ImageIcon className="mx-auto h-12 w-12 text-gray-400" />
          <p className="mt-2 text-gray-500">Zatím žádné obrázky v galerii</p>
          <p className="text-sm text-gray-400">
            Přidejte URL obrázků výše nebo použijte drag & drop
          </p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {value.map((image, index) => (
            <div
              key={index}
              draggable
              onDragStart={() => handleDragStart(index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragEnd={handleDragEnd}
              className={`group relative overflow-hidden rounded-lg border bg-white dark:bg-gray-800 ${
                draggedIndex === index
                  ? "border-primary-500 opacity-50"
                  : image.isPrimary
                    ? "border-primary-500 ring-2 ring-primary-500/20"
                    : "border-gray-200 dark:border-gray-700"
              }`}
            >
              {/* Image preview */}
              <div className="aspect-video bg-gray-100 dark:bg-gray-700">
                <img
                  src={image.url}
                  alt={image.alt || "Gallery image"}
                  className="h-full w-full object-cover"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src =
                      "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 100 100'%3E%3Crect fill='%23f3f4f6' width='100' height='100'/%3E%3Ctext x='50' y='50' text-anchor='middle' dy='.3em' fill='%239ca3af'%3EChyba%3C/text%3E%3C/svg%3E";
                  }}
                />
              </div>

              {/* Primary badge */}
              {image.isPrimary && (
                <div className="absolute left-2 top-2 rounded bg-primary-600 px-2 py-0.5 text-xs font-medium text-white">
                  Hlavní
                </div>
              )}

              {/* Drag handle */}
              <div className="absolute right-2 top-2 cursor-grab rounded bg-white/80 p-1 opacity-0 transition-opacity group-hover:opacity-100 dark:bg-gray-800/80">
                <GripVertical className="h-4 w-4 text-gray-500" />
              </div>

              {/* Actions */}
              <div className="p-3">
                <input
                  type="text"
                  value={image.alt || ""}
                  onChange={(e) => updateImage(index, { alt: e.target.value })}
                  placeholder="Popis obrázku (alt text)"
                  className="input input-sm mb-2 text-sm"
                />

                <div className="flex items-center justify-between">
                  {!image.isPrimary && (
                    <button
                      type="button"
                      onClick={() => setPrimary(index)}
                      className="text-xs text-primary-600 hover:text-primary-700"
                    >
                      Nastavit jako hlavní
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => removeImage(index)}
                    className="ml-auto p-1 text-error-500 hover:text-error-600"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <p className="text-sm text-gray-500">
        Tip: Přetáhněte obrázky pro změnu pořadí. První obrázek nebo označený
        jako "Hlavní" se zobrazí jako náhledový.
      </p>
    </div>
  );
}
